import { addDays, isAfter, isBefore, isValid } from 'date-fns';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BannerId } from '../../ActionCentre/Banners/banners';
import {
  ruleset,
  ServiceActionCodes,
  ServiceBannerConfig,
} from './utils/rulesetForServiceBanners';
import {
  useContractsQueryLegacy,
  useFinalStatementQuery,
  useMoveOutDateQuery,
} from '@/src/api';
import { findContractInLoss, isDualFuelContract } from '@/src/utils/contracts';
import {
  getShowSmartMeterBookingBannerStart,
  getSmartMeterBookingStart,
} from '@/src/redux/ducks/smartMeterBooking/smartMeterBooking';
import { EnergyContract } from '@/src/types/Account';
import { useFeature } from '@optimizely/react-sdk';

function setDismissed(id: BannerId, daysToExpiration: number) {
  const expiryDate = addDays(new Date(), daysToExpiration).toISOString();
  localStorage.setItem(id, expiryDate);
}

function isDismissed(id: BannerId): boolean {
  const storedValue = localStorage.getItem(id);
  if (storedValue) {
    const expiryDate = new Date(storedValue);
    if (isValid(expiryDate) && isAfter(expiryDate, new Date())) {
      return true;
    }
    localStorage.removeItem(id);
  }

  return false;
}

export const useServiceBanners = (
  accountId?: string | null,
): {
  bannerId: BannerId;
  actionCode: ServiceActionCodes;
  onDismiss: () => void;
}[] => {
  const [serviceBannerLossUpdatesEnabled] = useFeature(
    'enable_service_banner_loss_updates',
  );

  const dispatch = useDispatch();

  const contracts = useContractsQueryLegacy(accountId);
  const finalStatement = useFinalStatementQuery();
  const moveOut = useMoveOutDateQuery(accountId);

  useEffect(() => {
    dispatch(getSmartMeterBookingStart());
    dispatch(getShowSmartMeterBookingBannerStart());
  }, [dispatch]);

  if (contracts.status !== 'success' || moveOut.status !== 'success') {
    return [];
  }

  const electricityContractInLoss = findContractInLoss(
    contracts.data.electricity,
  );
  const gasContractInLoss = findContractInLoss(contracts.data.gas);

  const { electricity, gas } = contracts.data;
  const isDualFuel = isDualFuelContract(contracts.data);

  let inLoss: boolean;
  let inLossForDualFuel: boolean;

  if (serviceBannerLossUpdatesEnabled) {
    // New logic: use date comparison for loss detection
    const isContractInLoss = (contract: EnergyContract) => {
      // a contract is considered in loss if the following
      // status.inLoss flag is true
      // the lossDate is in the future and has not passed
      const today = new Date();
      const lossDate = new Date(contract.lossDate || '');

      return Boolean(contract.status?.inLoss && isBefore(today, lossDate));
    };

    const electricityInLoss = electricity.some(isContractInLoss);
    const gasInLoss = gas.some(isContractInLoss);
    inLoss = electricityInLoss || gasInLoss;
    inLossForDualFuel = electricityInLoss && gasInLoss;
  } else {
    // Old logic: use findContractInLoss results
    inLoss = Boolean(electricityContractInLoss || gasContractInLoss);
    inLossForDualFuel = Boolean(electricityContractInLoss && gasContractInLoss);
  }

  const lossCompleteForBothFuels = Boolean(
    electricityContractInLoss?.status?.lossComplete &&
      gasContractInLoss?.status?.lossComplete,
  );
  const hasAFinalStatement = serviceBannerLossUpdatesEnabled
    ? finalStatement.isSuccess &&
      Boolean(finalStatement.data?.electricity || finalStatement.data?.gas)
    : inLoss &&
      finalStatement.isSuccess &&
      Boolean(finalStatement.data?.electricity || finalStatement.data?.gas);
  const hasBothFinalStatements = serviceBannerLossUpdatesEnabled
    ? finalStatement.isSuccess &&
      Boolean(finalStatement.data?.electricity && finalStatement.data?.gas)
    : inLoss &&
      finalStatement.isSuccess &&
      Boolean(finalStatement.data?.electricity && finalStatement.data?.gas);
  const hasMoveOutDate = moveOut.data
    ? Boolean(moveOut.data.moveOutDate)
    : false;

  const rules = ruleset({
    inLoss,
    inLossForDualFuel,
    isDualFuel,
    hasMoveOutDate,
    hasAFinalStatement,
    hasBothFinalStatements,
    lossCompleteForBothFuels,
  });

  return rules
    .filter(({ id }) => !isDismissed(id))
    .reduce<ServiceBannerConfig[]>((acc, banner) => {
      banner.shown && acc.push(banner);
      return acc;
    }, [])
    .map(banner => ({
      bannerId: banner.id,
      actionCode: banner.actionCode,
      onDismiss: () => setDismissed(banner.id, banner.dismissDurationDays || 1),
    }));
};
